import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";

export const Logo = () => (
  <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M6 10L16 4L26 10L16 16L6 10Z" stroke="#9945FF" strokeWidth="2" strokeLinejoin="round" />
    <path d="M6 16L16 22L26 16" stroke="#14F195" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 22L16 28L26 22" stroke="#00F0FF" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/dashboard", label: "Basket Dashboard" },
  { to: "/rewards", label: "Rewards Checker" },
  { to: "/transparency", label: "Transparency" },
  { to: "/how-it-works", label: "How It Works" },
];

export const SOCIALS = {
  x: process.env.REACT_APP_X_URL || "https://x.com",
  telegram: process.env.REACT_APP_TELEGRAM_URL || "https://t.me",
};

const Nav = () => {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) =>
    `hover:text-[#14F195] transition-colors ${isActive ? "text-[#14F195]" : ""}`;

  return (
    <nav data-testid="main-nav" className="sticky top-0 z-50 bg-[#07050d]/85 backdrop-blur-md border-b border-[#9945FF]/20 px-6 py-4">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <Logo />
          <span className="display-font text-lg sm:text-2xl tracking-wide text-white uppercase whitespace-nowrap">Basket Protocol</span>
          <span className="mono-font text-xs bg-[#9945FF]/20 text-[#14F195] border border-[#9945FF]/40 px-2 py-0.5 rounded ml-1">$BASK</span>
        </Link>

        <div className="hidden lg:flex items-center gap-7 text-sm font-medium text-[#c4bcdc]">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={SOCIALS.x}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-join-community-btn"
            className="hidden sm:inline-block px-4 py-2 rounded-lg bg-gradient-to-r from-[#9945FF] to-[#14F195] text-black font-semibold text-xs tracking-wider uppercase hover:opacity-90 transition-opacity"
          >
            Join Community
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden mt-4 pb-2 flex flex-col gap-4 text-sm font-medium text-[#c4bcdc]">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkCls} end={item.to === "/"} onClick={() => setOpen(false)}>
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
};

const Footer = () => (
  <footer className="bg-[#050308] border-t border-white/5 py-12 px-6 text-xs text-[#8c85a8] mono-font">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <Logo />
        <span className="display-font text-lg text-white tracking-wide">Basket Protocol</span>
        <span className="text-[#9945FF]">$BASK</span>
      </div>
      <div data-testid="footer-disclaimer" className="text-center md:text-left text-[11px] text-[#7a7493] max-w-md">
        Disclaimer: Basket Protocol is a community ecosystem prototype. No guaranteed returns or profit expectations. All data shown is simulated for demonstration.
      </div>
      <div className="flex items-center gap-4">
        <a data-testid="footer-x-link" href={SOCIALS.x} target="_blank" rel="noopener noreferrer" className="hover:text-[#14F195] transition-colors">X / Twitter</a>
        <a data-testid="footer-telegram-link" href={SOCIALS.telegram} target="_blank" rel="noopener noreferrer" className="hover:text-[#14F195] transition-colors">Telegram</a>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#07050d] text-[#e6e2f8]">
      <Nav />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
